import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  medicalService,
  MedicalFilters,
  MedicalAppointmentCreate,
  MedicalAppointmentUpdate,
  MedicalAppointmentTypeCreate,
  MedicalAppointmentTypeUpdate,
} from '@/services/medicalAppointments';

// ---- Hooks para tipos de procedimiento ----

export function useMedicalTypes(category?: string, activeOnly?: boolean) {
  return useQuery({
    queryKey: ['medical', 'types', category, activeOnly],
    queryFn: () => medicalService.getTypes(category, activeOnly),
  });
}

export function useCreateMedicalType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MedicalAppointmentTypeCreate) => medicalService.createType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'types'] });
    },
  });
}

export function useUpdateMedicalType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MedicalAppointmentTypeUpdate }) =>
      medicalService.updateType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'types'] });
    },
  });
}

export function useDeleteMedicalType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => medicalService.deleteType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'types'] });
    },
  });
}

// ---- Hooks para turnos médicos ----

export function useMedicalAppointments(filters?: MedicalFilters) {
  return useQuery({
    queryKey: ['medical', 'appointments', filters],
    queryFn: () => medicalService.getAppointments(filters),
  });
}

export function useMedicalCalendar(dateFrom: string, dateTo: string, category?: string) {
  return useQuery({
    queryKey: ['medical', 'calendar', dateFrom, dateTo, category],
    queryFn: () => medicalService.getCalendar(dateFrom, dateTo, category),
    enabled: !!dateFrom && !!dateTo,
  });
}

export function useMedicalAppointment(id: number | null) {
  return useQuery({
    queryKey: ['medical', 'appointment', id],
    queryFn: () => medicalService.getAppointment(id!),
    enabled: !!id,
  });
}

export function useCreateMedicalAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MedicalAppointmentCreate) => medicalService.createAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical'] });
    },
  });
}

export function useUpdateMedicalAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MedicalAppointmentUpdate }) =>
      medicalService.updateAppointment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical'] });
    },
  });
}

export function useCompleteMedicalAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => medicalService.completeAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical'] });
    },
  });
}

export function useCancelMedicalAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => medicalService.cancelAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical'] });
    },
  });
}

export function useDeleteMedicalAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => medicalService.deleteAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical'] });
    },
  });
}
